import * as htmlToImage from 'html-to-image';
import { SEARCH_FIELDS } from '../data/constants';
import { toast } from '@pella/sash-framework';

/**
 * Get the name of the EBS instance based on the API URL.
 * @returns {string} The name of the EBS instance.
 */
export const getInstanceName = () => {
  const apiUrl = window.REACT_APP_API_URL;

  if (apiUrl.includes('apidev')) {
    return 'EBSDEV3';
  } else if (apiUrl.includes('apiqa')) {
    return 'EBSQA';
  }

  return 'EBSPROD';
};

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * Convert an ISO date string to a formatted date string.
 * @param {string} iso - The ISO date string.
 * @returns {string} The formatted date string.
 */
export const convertISO = (iso) => dateFormatter.format(new Date(iso));

/**
 * Helper function to get nested property value from an object
 * @param {object} obj - The object to traverse
 * @param {string} path - The dot-separated path to the property
 * @returns {unknown} The value at the specified path
 */
const getNestedValue = (obj, path) => {
  const parts = path.split('.');
  let current = obj;

  for (const part of parts) {
    current = current?.[part];
  }

  return current;
};

/**
 * Helper function to collect every primitive value as a string, recursing into objects and arrays
 * @param {unknown} value - The value to collect from
 * @returns {string[]} The primitive values as strings
 */
const getSearchableValues = (value) => {
  if (value === undefined || value === null) return [];

  if (typeof value === 'object') return Object.values(value).flatMap((entry) => getSearchableValues(entry));

  return [String(value)];
};

/**
 * Search multiple fields in an array of objects with nested support
 * @param {string} searchValue - The value to search for.
 * @param {Pella.ProductionRequestAPI.Request[]} [data] - The array of objects to search.
 * @returns {Pella.ProductionRequestAPI.Request[] | undefined} The filtered array of objects.
 */
export const searchMultiFields = (searchValue, data) => {
  if (!searchValue || !data) return data;

  const lowerSearchValue = searchValue.toLowerCase();

  const filtered = data?.filter((item) =>
    SEARCH_FIELDS.some((field) =>
      getSearchableValues(getNestedValue(item, field)).some((value) => value.toLowerCase().includes(lowerSearchValue)),
    ),
  );

  return filtered.length ? filtered : undefined;
};

/**
 * Get combined active queues from permanent and temporary queues
 * @param {Pella.ProductionRequestAPI.ResourceQueueByStatus[] | null} permanentQueues - The permanent queues
 * @param {Pella.ProductionRequestAPI.ResourceQueueByStatus[] | null} temporaryQueues - The temporary queues
 * @returns {string[]} The list of active queue IDs
 */
export const getActiveQueueIds = (permanentQueues, temporaryQueues) =>
  [...(permanentQueues ?? []), ...(temporaryQueues ?? [])].map((q) => q.queueId);

/**
 * Remove a request from the cache data
 * @param {Pella.ProductionRequestAPI.GetRequestsOutput} oldData - The old cached data
 * @param {Pella.ProductionRequestAPI.Request['id']} id - The ID of the request to remove
 * @returns {Pella.ProductionRequestAPI.GetRequestsOutput} The new cached data with the request removed
 */
export const removeRequestFromCache = (oldData, id) => {
  if (!oldData) return oldData;

  const { accepted, available, onhold } = oldData;
  const newData = {};

  if (accepted) newData.accepted = accepted.filter((req) => req.id !== id);

  if (available) newData.available = available.filter((req) => req.id !== id);

  if (onhold) newData.onhold = onhold.filter((req) => req.id !== id);

  return newData;
};

/**
 * Replace a request's communication history in the cache data. The request stays in
 * whichever status group it already occupies — only the communications field changes.
 * @param {Pella.ProductionRequestAPI.GetRequestsOutput} oldData - The old cached data
 * @param {Pella.ProductionRequestAPI.Request['id']} id - The ID of the request to update
 * @param {Local.Communication[]} communications - The full communication history to set
 * @returns {Pella.ProductionRequestAPI.GetRequestsOutput} The new cached data
 */
export const setRequestCommunicationInCache = (oldData, id, communications) => {
  if (!oldData) return oldData;

  const { accepted, available, onhold } = oldData;

  /**
   * Applies the new communication history to the matching request in one status group.
   * @param {Pella.ProductionRequestAPI.Request[]} requests - The requests in a status group
   * @returns {Pella.ProductionRequestAPI.Request[]} The group with the target request updated
   */
  const applyToGroup = (requests) => requests.map((req) => (req.id === id ? { ...req, communications } : req));

  const newData = {};

  if (accepted) newData.accepted = applyToGroup(accepted);

  if (available) newData.available = applyToGroup(available);

  if (onhold) newData.onhold = applyToGroup(onhold);

  return newData;
};

/**
 * Normalizes identifiers to lower case for case-insensitive comparisons.
 * @param {string | null | undefined} value - User identifier to normalize.
 * @returns {string} Normalized identifier.
 */
export const normalizeToLower = (value) => (value ?? '').trim().toLowerCase();

/**
 * Computes total and unread communication counts for a request and a specific user.
 * Entries authored by the current user are excluded from the unread count.
 * @param {Local.RequestWithCommunication} request - Request carrying communication history.
 * @param {string | null | undefined} userid - Current user identifier.
 * @returns {{ total: number, unread: number }} Communication count summary.
 */
export const getCommunicationCounts = (request, userid) => {
  const communications = request.communications ?? [];
  const normalizedUserid = normalizeToLower(userid);

  if (!normalizedUserid) {
    return { total: communications.length, unread: 0 };
  }

  let unread = 0;

  for (const entry of communications) {
    const submittedByCurrentUser = normalizeToLower(entry.userName) === normalizedUserid;
    if (submittedByCurrentUser) {
      continue;
    }

    const isRead = (entry.readBy ?? []).some((reader) => normalizeToLower(reader) === normalizedUserid);

    if (!isRead) {
      unread += 1;
    }
  }

  return { total: communications.length, unread };
};

/**
 * Safely parse a JSON string into an object
 * @param {string} jsonString - The JSON string to parse
 * @returns {object | null} The parsed object or null if parsing fails
 */
export const safeJSONParse = (jsonString) => {
  try {
    const parse = JSON.parse(jsonString);

    if (typeof parse !== 'object' || parse === null) return null;

    return parse;
  } catch {
    return null;
  }
};

/**
 * Download the content of a ref as a PNG image
 * @param {Local.RequestBarcodeProps['ref']} ref - The ref to the HTML element
 */
export const downloadRef = async (ref) => {
  if (!ref.current) return;

  try {
    const dataUrl = await htmlToImage.toPng(ref.current);

    const link = document.createElement('a');
    link.download = 'production-request-barcode.png';
    link.href = dataUrl;
    link.click();
    link.remove();
  } catch {
    toast.error('Failed to download image. Please try again.');
  }
};
/**
 * Deterministic hash function for generating a compact unread communications signature.
 * @param {string} str - String to hash.
 * @returns {string} Hex-encoded hash.
 */
export const fnv1aHash = (str) => {
  let hash = 2_166_136_261;
  const modulo = 4_294_967_291;

  for (const char of str) {
    hash = (hash * 16_777_619 + Number(char.codePointAt(0))) % modulo;
  }

  return hash.toString(16);
};
